'use client';

import { useCallback, useMemo, useState } from 'react';

/**
 * Multi-select state for bulk hand-over / take-back (EH-002, EH-005).
 *
 * Selection is keyed by institution id and held here rather than in the list, so
 * the "select all" checkbox, the row checkboxes and the action bar all read one
 * source. `selectableIds` is the *currently visible* set — select-all must never
 * reach rows the user cannot see, which is how a filtered select-all silently
 * hands over more than was on screen.
 */
export function useHandlingSelection(selectableIds: number[]) {
  // Lazy: `new Set()` would allocate a throwaway on every render otherwise.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  // Drop ids that have scrolled out of the selectable set (filter changed, row
  // removed) rather than carrying a stale id into a bulk write.
  const effectiveSelected = useMemo(() => {
    const visible = new Set(selectableIds);
    return new Set(Array.from(selectedIds).filter((id) => visible.has(id)));
  }, [selectedIds, selectableIds]);

  const toggle = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const allSelected =
    selectableIds.length > 0 && effectiveSelected.size === selectableIds.length;
  const someSelected = effectiveSelected.size > 0 && !allSelected;

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const visible = new Set(selectableIds);
      const selectedVisible = Array.from(prev).filter((id) => visible.has(id));
      return selectedVisible.length === selectableIds.length
        ? new Set()
        : new Set(selectableIds);
    });
  }, [selectableIds]);

  return {
    selectedIds: effectiveSelected,
    selectedCount: effectiveSelected.size,
    isSelected: (id: number) => effectiveSelected.has(id),
    toggle,
    toggleAll,
    clear,
    allSelected,
    someSelected
  };
}
